import { Navigate, useLocation, useSearchParams } from 'react-router-dom'
import { searchStringToObject, objectToSearchString } from 'serialize-query-params'
import { boolean } from 'boolean'

import Loader from 'components/atoms/Loader'
import { useAuth } from 'hooks/useAuth'
import { useProfileCompletionBlocking } from 'hooks/useProfileCompletionRequired'

export function PasskeyRegistrationGuard({ children }: { children: JSX.Element }) {
	const location = useLocation()
	const [searchParams] = useSearchParams()
	const a = searchParams.get('a')
	const to = searchParams.get('to')
	const searchParamsString = objectToSearchString({ a, to })
	const { from, inviteId } = location.state || {}
	const { token, requireOtp, authInfo } = useAuth()
	const profileCompletionBlocking = useProfileCompletionBlocking()

	if (requireOtp) {
		return (
			<Navigate
				to={`/auth/mfa${searchParamsString ? `?${searchParamsString}` : ''}`}
				state={{ from: location, inviteId }}
				replace
			/>
		)
	} else {
		if (token && authInfo) {
			const { isDeleted, isComplete, skipPasskeyRegistration, isPhoneNumberVerified } = authInfo
			if (boolean(isDeleted)) {
				return (
					<Navigate
						to={`/auth/recover${searchParamsString ? `?${searchParamsString}` : ''}`}
						state={{ from: location, inviteId }}
						replace
					/>
				)
			}
			// If user has already registered passkey or skipped it, redirect them
			if (boolean(isComplete) && boolean(skipPasskeyRegistration)) {
				if (from?.pathname) {
					const { urlToken, ...params } = searchStringToObject(from.search) || {}
					const searchString = objectToSearchString(params)
					if (urlToken) {
						return <Navigate to={`${from.pathname}${searchString ? '?' + searchString : ''}`} replace />
					}
				}
				if (inviteId) {
					return <Navigate to={`/accept-invite/${inviteId}`} state={{ from: location }} replace />
				}
				if (['join-now'].includes(a!)) {
					return <Navigate to={`/pie-hub?a=${a}`} replace />
				}
				if (to) {
					return <Navigate to={to} replace />
				}

				if (
					!boolean(isPhoneNumberVerified) &&
					import.meta.env.VITE_ENABLE_FEATURE_VERIFY_PHONE_NUMBER === 'true'
				) {
					return (
						<Navigate
							to={`/auth/verify-phone-number${searchParamsString ? `?${searchParamsString}` : ''}`}
							state={{ from: location, inviteId }}
							replace
						/>
					)
				}

				return <Navigate to="/" state={{ from: location }} replace />
			}
		} else {
			return <Navigate to="/auth/login" state={{ from: location }} replace />
		}
	}
	// The profile-completion modal comes first: hold the page back until the profile says whether it is needed
	// and while it is up, so only one dialog shows at a time. It appears once the modal is completed.
	if (profileCompletionBlocking) {
		return (
			<div role="status">
				<Loader />
				<span className="sr-only">Loading</span>
			</div>
		)
	}
	return children
}
